import { ReactNode } from 'react';
import { Button, Flex } from '@radix-ui/themes';

export type FilterOption = {
  label: string;
  value: string;
  icon?: ReactNode;
  disabled?: boolean;
};

type Props = {
  options: FilterOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  buttonSize?: 'small' | 'medium' | 'large';
  size?: '1' | '2' | '3';
  maxSelections?: number;
  allowMultiple?: boolean;
  style?: 'compact' | 'full';
  visible?: boolean;
  gap?: string;
  justify?: 'start' | 'center' | 'end';
  wrap?: 'wrap' | 'nowrap';
};

const FilterToggleGroup = ({
  options,
  selectedValues,
  onSelectionChange,
  buttonSize = 'large',
  size = '3',
  maxSelections,
  allowMultiple = true,
  style = 'full',
  visible = true,
  gap = '2',
  justify = 'center',
  wrap = 'wrap',
}: Props) => {
  if (!visible) return null;

  const handleOptionClick = (value: string) => {
    if (!allowMultiple) {
      // Single selection mode
      onSelectionChange([value]);
      return;
    }

    // Multiple selection mode
    const currentIndex = selectedValues.indexOf(value);
    let newSelection: string[];

    if (currentIndex === -1) {
      // Add to selection
      if (maxSelections && selectedValues.length >= maxSelections) {
        // Replace last selection if at max
        newSelection = [...selectedValues.slice(0, -1), value];
      } else {
        newSelection = [...selectedValues, value];
      }
    } else {
      // Remove from selection
      newSelection = selectedValues.filter(v => v !== value);
    }

    onSelectionChange(newSelection);
  };

  const getButtonSize = () => {
    if (buttonSize === 'small') return '1';
    if (buttonSize === 'medium') return '2';
    return '3';
  };

  const getMinWidth = () => {
    if (buttonSize === 'small') return '64px';
    if (buttonSize === 'medium') return '100px';
    return '140px';
  };

  return (
    <Flex
      align="center"
      justify={justify}
      gap={gap}
    >
      {options.map((option) => {
        const isSelected = selectedValues.includes(option.value);
        const isDisabled = option.disabled;

        return (
          <Button
            key={option.value}
            size={getButtonSize()}
            variant={isSelected ? 'solid' : 'surface'}
            color={isSelected ? 'indigo' : 'iris'}
            onClick={() => handleOptionClick(option.value)}
            disabled={isDisabled}
            style={{ 
              minWidth: getMinWidth(),
              display: 'inline-flex',
              alignItems: 'center',
              gap: option.icon ? '6px' : undefined,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              boxShadow: isSelected ? '0 2px 8px rgba(99, 102, 241, 0.25)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
              borderWidth: isSelected ? '1px' : '1px',
              borderStyle: 'solid',
              borderColor: isSelected ? 'var(--indigo-7)' : 'var(--gray-6)',
              transition: 'all 0.2s ease-in-out',
            }}
            className={isSelected ? 'filter-button-active' : 'filter-button-inactive'}
          >
            {option.icon}
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
};

export default FilterToggleGroup;